/**
 * Typed `httpsCallable` wrappers.
 *
 * One wrapper per entry in `CALLABLES` (the single source of truth for callable
 * names, from `@agente/shared`). Each wrapper is typed with the shared
 * request/response contracts and returns the unwrapped `.data` payload so callers
 * don't deal with the `HttpsCallableResult` envelope.
 */
import {
  type AdminGetPricingResponse,
  type AdminGetUserRequest,
  type AdminGetUserResponse,
  type AdminListUsersRequest,
  type AdminListUsersResponse,
  type AdminSetUserFeaturesRequest,
  type AdminSetUserFeaturesResponse,
  type AdminUpdatePricingRequest,
  type AdminUpdatePricingResponse,
  CALLABLES,
  type CreateReminderRequest,
  type CreateReminderResponse,
  type DisconnectConnectedAccountRequest,
  type DisconnectConnectedAccountResponse,
  type DisconnectMicrosoftResponse,
  type ExecuteAgentToolRequest,
  type ExecuteAgentToolResponse,
  type GetGoogleAuthUrlRequest,
  type GetGoogleAuthUrlResponse,
  type GetMicrosoftAuthUrlRequest,
  type GetMicrosoftAuthUrlResponse,
  type GetMicrosoftExtraAuthUrlRequest,
  type GetMicrosoftExtraAuthUrlResponse,
  type GetUploadUrlRequest,
  type GetUploadUrlResponse,
  type GetVoiceSessionConfigRequest,
  type GetVoiceSessionConfigResponse,
  type LinkWhatsappRequest,
  type LinkWhatsappResponse,
  type RecordVoiceUsageRequest,
  type RecordVoiceUsageResponse,
  type RefreshAdminClaimResponse,
  type SendChannelTestRequest,
  type SendChannelTestResponse,
  type SendMessageRequest,
  type SendMessageResponse,
  type SyncMicrosoftPhotoResponse,
  type UnlinkWhatsappRequest,
  type UnlinkWhatsappResponse,
} from '@agente/shared'
import { httpsCallable } from 'firebase/functions'

import { functions } from './firebase'

/** Send a chat turn. The assistant reply streams into Firestore; this resolves with the message ids. */
export const sendMessage = httpsCallable<SendMessageRequest, SendMessageResponse>(
  functions,
  CALLABLES.sendMessage,
)

/** Begin the "Connect Microsoft 365" backend OAuth flow. Returns an `authUrl` to redirect to. */
export const getMicrosoftAuthUrl = httpsCallable<
  GetMicrosoftAuthUrlRequest,
  GetMicrosoftAuthUrlResponse
>(functions, CALLABLES.getMicrosoftAuthUrl)

/** Revoke/forget the stored Microsoft refresh token. Takes no arguments. */
export const disconnectMicrosoft = httpsCallable<void, DisconnectMicrosoftResponse>(
  functions,
  CALLABLES.disconnectMicrosoft,
)

/** Fetch the Graph profile photo and store it on the user doc. Takes no arguments. */
export const syncMicrosoftPhoto = httpsCallable<void, SyncMicrosoftPhotoResponse>(
  functions,
  CALLABLES.syncMicrosoftPhoto,
)

/** Create a reminder directly (mirrors the agent tool). */
export const createReminder = httpsCallable<CreateReminderRequest, CreateReminderResponse>(
  functions,
  CALLABLES.createReminder,
)

/** Get a signed URL for direct attachment upload to Storage. */
export const getUploadUrl = httpsCallable<GetUploadUrlRequest, GetUploadUrlResponse>(
  functions,
  CALLABLES.getUploadUrl,
)

/** Execute one server-side agent tool on behalf of the client voice session. */
export const executeAgentTool = httpsCallable<ExecuteAgentToolRequest, ExecuteAgentToolResponse>(
  functions,
  CALLABLES.executeAgentTool,
)

/** Model id + system prompt for the client-side Gemini Live voice session. */
export const getVoiceSessionConfig = httpsCallable<
  GetVoiceSessionConfigRequest,
  GetVoiceSessionConfigResponse
>(functions, CALLABLES.getVoiceSessionConfig)

/** Reconcilia o claim `admin` do próprio chamador. Sem argumentos. */
export const refreshAdminClaim = httpsCallable<void, RefreshAdminClaimResponse>(
  functions,
  CALLABLES.refreshAdminClaim,
)

/** Lista paginada de usuários com uso/custo agregado (somente admin). */
export const adminListUsers = httpsCallable<AdminListUsersRequest, AdminListUsersResponse>(
  functions,
  CALLABLES.adminListUsers,
)

/** Detalhe de um usuário, incluindo seu uso diário (somente admin). */
export const adminGetUser = httpsCallable<AdminGetUserRequest, AdminGetUserResponse>(
  functions,
  CALLABLES.adminGetUser,
)

/** Parâmetros de precificação vigentes (somente admin). Sem argumentos. */
export const adminGetPricing = httpsCallable<void, AdminGetPricingResponse>(
  functions,
  CALLABLES.adminGetPricing,
)

/** Atualiza a cotação USD→BRL usada no cálculo de custo (somente admin). */
export const adminUpdatePricing = httpsCallable<
  AdminUpdatePricingRequest,
  AdminUpdatePricingResponse
>(functions, CALLABLES.adminUpdatePricing)

/** Liga/desliga flags de feature por usuário (somente admin). */
export const adminSetUserFeatures = httpsCallable<
  AdminSetUserFeaturesRequest,
  AdminSetUserFeaturesResponse
>(functions, CALLABLES.adminSetUserFeatures)

/** Registra o consumo de áudio (segundos) de uma sessão de voz encerrada, para custo por pessoa. */
export const recordVoiceUsage = httpsCallable<RecordVoiceUsageRequest, RecordVoiceUsageResponse>(
  functions,
  CALLABLES.recordVoiceUsage,
)

/** Vincula um número de WhatsApp ao usuário (via contato GHL). */
export const linkWhatsapp = httpsCallable<LinkWhatsappRequest, LinkWhatsappResponse>(
  functions,
  CALLABLES.linkWhatsapp,
)

/** Remove o vínculo de WhatsApp do usuário. Sem argumentos. */
export const unlinkWhatsapp = httpsCallable<UnlinkWhatsappRequest, UnlinkWhatsappResponse>(
  functions,
  CALLABLES.unlinkWhatsapp,
)

/** Inicia a conexão de uma conta Google como fonte de dados adicional (slot `google-1`). */
export const getGoogleAuthUrl = httpsCallable<GetGoogleAuthUrlRequest, GetGoogleAuthUrlResponse>(
  functions,
  CALLABLES.getGoogleAuthUrl,
)

/** Inicia a conexão de uma conta Microsoft corporativa extra (slots `ms-extra-1..3`). */
export const getMicrosoftExtraAuthUrl = httpsCallable<
  GetMicrosoftExtraAuthUrlRequest,
  GetMicrosoftExtraAuthUrlResponse
>(functions, CALLABLES.getMicrosoftExtraAuthUrl)

/** Desvincula uma conta conectada (Google ou Microsoft extra) por slot. */
export const disconnectConnectedAccount = httpsCallable<
  DisconnectConnectedAccountRequest,
  DisconnectConnectedAccountResponse
>(functions, CALLABLES.disconnectConnectedAccount)

/** Dispara uma mensagem de teste avulsa num canal de entrega (Teams/e-mail/WhatsApp). */
export const sendChannelTest = httpsCallable<SendChannelTestRequest, SendChannelTestResponse>(
  functions,
  CALLABLES.sendChannelTest,
)

/** All callable wrappers grouped for convenient import. */
export const callables = {
  sendMessage,
  getMicrosoftAuthUrl,
  disconnectMicrosoft,
  syncMicrosoftPhoto,
  createReminder,
  getUploadUrl,
  executeAgentTool,
  getVoiceSessionConfig,
  refreshAdminClaim,
  adminListUsers,
  adminGetUser,
  adminGetPricing,
  adminUpdatePricing,
  adminSetUserFeatures,
  recordVoiceUsage,
  linkWhatsapp,
  unlinkWhatsapp,
  getGoogleAuthUrl,
  getMicrosoftExtraAuthUrl,
  disconnectConnectedAccount,
  sendChannelTest,
} as const
